import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useCompany } from '../../../context/CompanyContext';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

// Curated IANA list — US + a few common adjacent zones. Keeps the dropdown
// short and scannable. A tenant outside this list can be extended later.
const TIMEZONES = [
  { value: 'America/New_York',     label: 'Eastern Time — New York' },
  { value: 'America/Chicago',      label: 'Central Time — Chicago' },
  { value: 'America/Denver',       label: 'Mountain Time — Denver' },
  { value: 'America/Phoenix',      label: 'Mountain Time (no DST) — Phoenix' },
  { value: 'America/Los_Angeles',  label: 'Pacific Time — Los Angeles' },
  { value: 'America/Anchorage',    label: 'Alaska Time — Anchorage' },
  { value: 'Pacific/Honolulu',     label: 'Hawaii Time — Honolulu' },
  { value: 'America/Toronto',      label: 'Eastern Time — Toronto' },
  { value: 'America/Vancouver',    label: 'Pacific Time — Vancouver' },
  { value: 'UTC',                  label: 'UTC' },
];

const _deviceTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

const TimezoneSettings = () => {
  const { companyId } = useCompany();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [value, setValue]     = useState('');
  const [saved, setSaved]     = useState('');
  const deviceTz = _deviceTz();

  // Load current timezone from companies/{companyId}.timezone
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId));
        if (cancelled) return;
        const tz = snap.exists() ? ((snap.data() || {}).timezone || '') : '';
        setValue(tz);
        setSaved(tz);
      } catch (err) {
        console.error('[TimezoneSettings] load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId || saving) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        timezone: value,
        updatedAt: serverTimestamp(),
      });
      setSaved(value);
      toast.success('Company timezone saved');
    } catch (err) {
      console.error('[TimezoneSettings] save error:', err);
      toast.error('Failed to save timezone — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUseDevice = () => setValue(deviceTz);

  if (loading) return <p className="text-sm text-gray-400">Loading timezone…</p>;

  const isDirty = value !== saved;
  const isKnownTz = TIMEZONES.some(t => t.value === value);

  return (
    <div className="space-y-5" data-testid="timezone-settings-section">
      <div>
        <Label className="text-sm font-medium text-gray-900">Company timezone</Label>
        <p className="text-xs text-gray-500 mt-0.5 max-w-md">
          Used to anchor “today” on technician mobile views, schedule day-boundaries,
          and any time-of-day copy. If left unset, each device falls back to its own local timezone.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 max-w-md">
        <Select value={value || undefined} onValueChange={setValue}>
          <SelectTrigger
            className="w-full sm:w-80"
            data-testid="timezone-select-trigger"
          >
            <SelectValue placeholder="Select a timezone" />
          </SelectTrigger>
          <SelectContent data-testid="timezone-select-content">
            {TIMEZONES.map(tz => (
              <SelectItem
                key={tz.value}
                value={tz.value}
                data-testid={`timezone-option-${tz.value.replace(/\//g, '-')}`}
              >
                {tz.label}
              </SelectItem>
            ))}
            {value && !isKnownTz && (
              <SelectItem value={value} data-testid="timezone-option-current">
                {value} (current)
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span>
          Your device is in <span className="font-semibold text-gray-700">{deviceTz}</span>.
        </span>
        {value !== deviceTz && (
          <button
            type="button"
            onClick={handleUseDevice}
            className="text-xs font-medium text-[var(--cb-orange)] hover:underline"
            data-testid="timezone-use-device-btn"
          >
            Use device timezone
          </button>
        )}
      </div>

      <Button
        size="sm"
        onClick={handleSave}
        disabled={saving || !isDirty || !value}
        data-testid="save-timezone-btn"
      >
        {saving ? 'Saving…' : 'Save Timezone'}
      </Button>

      {saved && !isDirty && (
        <p className="text-xs text-emerald-700" data-testid="timezone-saved-indicator">
          Saved — “today” is anchored to {saved}.
        </p>
      )}
    </div>
  );
};

export default TimezoneSettings;
